import { hubAxios } from "@helpers/lib/axios";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { getCurrentLocale } from "@memorang/helpers/i18n";
import type { BaseItemSchema, OriginalItemTypes } from "@memorang/types";
import SuperJSON from "superjson";
import type { PteWorkflowUserResponse } from "../types/pte-assessor";
import { buildAdditionalContextBody } from "./extract-image-files";
import { extractItemContent } from "./extract-item-content";
import { buildSessionScopeBody } from "./session-scope-body";

export type AssessmentCriterionResult = {
	id: string;
	name: string;
	score: number;
	displayScore?: number;
	maxScore: number;
	normalizedScore?: number;
	multiplier?: number;
	summary: string;
};

export type AssessmentResultDTO = {
	criteria: AssessmentCriterionResult[];
	overall: {
		result: number;
		displayResult?: number;
		maxPoints: number;
		normalizedResult?: number;
	};
	feedback: string;
	transcription?: string;
};

export type AssessorRequestParams = {
	itemId: number;
	itemTypeId: OriginalItemTypes;
	item: BaseItemSchema;
	userResponse: PteWorkflowUserResponse;
	userId: string;
	projectId: number;
	sessionId?: number;
	examId?: string;
};

// Single attempt; the runner owns retries so it can seq-guard each.
export const fetchAssessorResult = async (
	params: AssessorRequestParams,
): Promise<AssessmentResultDTO | null> => {
	const {
		itemId,
		itemTypeId,
		item,
		userResponse,
		userId,
		projectId,
		sessionId,
		examId,
	} = params;

	const additionalContextBody = buildAdditionalContextBody(item);

	try {
		const { data } = await hubAxios.post<AssessmentResultDTO>(
			HUB_ENDPOINTS.ASSESSOR_AGENT,
			{
				stream: false,
				inputs: SuperJSON.serialize({
					itemTypes: [itemTypeId],
					item: { id: String(itemId), content: extractItemContent(item) },
					userResponse,
					runMode: "full",
					outputLanguage: getCurrentLocale(),
				}),
				...additionalContextBody,
				userId,
				projectId,
				...buildSessionScopeBody({ sessionId, examId }),
			},
		);

		return data;
	} catch {
		return null;
	}
};
